import React, { memo, type ReactNode } from 'react';
import { isEqual } from 'lodash-es';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import { Loader } from '../../Elements/Loader/index.tsx';
import { HiddenRadio, RadioContainer, RadioLabel, RadioWrapper, StyledRadio, Wrapper } from './styled.ts';
import type { RadioGroupProps } from './types.ts';

type RadioGroupComponentType = <TValue>(props: RadioGroupProps<TValue> & { ref?: React.ForwardedRef<HTMLDivElement> }) => ReactNode;

export const RadioGroup = memo(
  React.forwardRef(function RadioGroup<TValue>(props: RadioGroupProps<TValue>, ref: React.ForwardedRef<HTMLDivElement>) {
    const { options, selectedValue, onChange, disabled: allDisabled, isLoading, formatOptionLabel, dataTestId, withLabelMargin } = props;

    const handleChange = useCallbackRef((value: TValue) => () => onChange(value));

    return (
      <Wrapper ref={ref} tabIndex={0} data-testid={dataTestId} withLabelMargin={withLabelMargin}>
        {isLoading ? (
          <Loader />
        ) : (
          options.map((option) => {
            const checked = isEqual(option.value, selectedValue);
            const disabled = allDisabled || option.disabled;

            return (
              <RadioContainer
                key={option.label}
                disabled={disabled}
                data-testid={getDataTestId(option.label, `${option.value}${checked ? '-selected' : ''}`)}
                onClick={disabled ? undefined : handleChange(option.value)}>
                <RadioWrapper>
                  <HiddenRadio type='radio' checked={checked} disabled={disabled} readOnly />
                  <StyledRadio checked={checked} disabled={disabled} />
                </RadioWrapper>
                <RadioLabel disabled={disabled}>{formatOptionLabel ? formatOptionLabel(option) : option.label}</RadioLabel>
              </RadioContainer>
            );
          })
        )}
      </Wrapper>
    );
  }),
) as RadioGroupComponentType;
